// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useEffect, useState, type CSSProperties } from 'react'
import { Plug, Loader2, AlertCircle, RefreshCw, Check, X } from 'lucide-react'
import * as api from '../../api/client'

const MUTE = 'var(--color-text-mute, #6b7280)'
const BORDER = 'var(--color-border, #e5e7eb)'

function YesNo({ ok }: { ok: boolean }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
      color: ok ? '#15803d' : '#b91c1c', background: ok ? '#dcfce7' : '#fee2e2', padding: '2px 8px', borderRadius: 999 }}>
      {ok ? <Check size={12} /> : <X size={12} />}{ok ? 'configured' : 'missing'}
    </span>
  )
}

export default function ApiConfigTab() {
  const [data, setData] = useState<api.ApiConfigStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { void load() }, [])
  async function load() {
    setLoading(true); setError(null)
    try { setData(await api.getApiConfig()) }
    catch (e: any) { setError(e?.message || 'Failed to load API configuration') }
    finally { setLoading(false) }
  }

  return (
    <div style={{ maxWidth: '100%' }}>
      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, margin: '0 0 4px' }}>
            <Plug size={18} /> API configuration
          </h2>
          <p style={{ margin: 0, fontSize: 13, color: MUTE }}>
            External integrations, operational settings, and secret presence. Read-only — secret values are never shown.
          </p>
        </div>
        <button onClick={() => void load()} disabled={loading} style={iconBtn} title="Refresh"><RefreshCw size={14} className={loading ? 'spin' : undefined} /></button>
      </header>

      {error && <div style={errBox}><AlertCircle size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />{error}</div>}

      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: MUTE }}><Loader2 size={18} className="spin" /> Loading…</div>
      ) : !data ? null : (
        <>
          <h3 style={h3}>Integrations</h3>
          <div style={box}>
            {data.integrations.map((it, i) => (
              <div key={it.name} style={{ ...row, borderTop: i ? `1px solid ${BORDER}` : undefined }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>{it.name}</div>
                  <div style={{ fontSize: 12, color: MUTE }}>{it.detail}</div>
                </div>
                <YesNo ok={it.configured} />
              </div>
            ))}
          </div>

          <h3 style={h3}>Operational settings</h3>
          <div style={box}>
            {data.settings.map((s, i) => (
              <div key={s.key} style={{ ...row, borderTop: i ? `1px solid ${BORDER}` : undefined }}>
                <span style={{ flex: 1, fontSize: 13 }}>{s.key}</span>
                <span style={{ fontSize: 13, fontFamily: 'ui-monospace, Menlo, monospace', color: 'var(--color-text, #111827)' }}>{s.value}</span>
              </div>
            ))}
          </div>

          <h3 style={h3}>Secrets</h3>
          <div style={box}>
            {data.secrets.map((s, i) => (
              <div key={s.name} style={{ ...row, borderTop: i ? `1px solid ${BORDER}` : undefined }}>
                <span style={{ flex: 1, fontSize: 13 }}>{s.name}</span>
                <span style={{ fontSize: 12, color: MUTE, marginRight: 4 }}>{s.source}</span>
                <YesNo ok={s.present} />
              </div>
            ))}
          </div>

          <p style={{ fontSize: 11, color: MUTE, marginTop: 16 }}>{data.note}</p>
        </>
      )}
    </div>
  )
}

const box: CSSProperties = { border: `1px solid ${BORDER}`, borderRadius: 8, overflow: 'hidden' }
const row: CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px' }
const h3: CSSProperties = { fontSize: 14, margin: '22px 0 8px', fontWeight: 600 }
const iconBtn: CSSProperties = { width: 32, height: 32, border: `1px solid ${BORDER}`, borderRadius: 6, background: 'transparent', color: 'var(--color-text, #111827)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }
const errBox: CSSProperties = { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 12px', borderRadius: 6, fontSize: 13, marginBottom: 12 }
